import { useRef } from "react";
import { motion, useInView } from "framer-motion";

/**
 * Reveal — scroll-triggered entrance animation.
 * Variants: "up" (default), "fade", "clip", "scale"
 */
export default function Reveal({
  children,
  variant = "up",
  delay = 0,
  duration = 0.9,
  once = true,
  amount = 0.25,
  className = "",
  as = "div",
}) {
  const ref = useRef(null);
  const inView = useInView(ref, { once, amount });

  const variants = {
    up: {
      hidden: { opacity: 0, y: 40 },
      show: { opacity: 1, y: 0 },
    },
    fade: {
      hidden: { opacity: 0 },
      show: { opacity: 1 },
    },
    clip: {
      hidden: { opacity: 0, clipPath: "inset(0 0 100% 0)" },
      show: { opacity: 1, clipPath: "inset(0 0 0% 0)" },
    },
    scale: {
      hidden: { opacity: 0, scale: 0.92 },
      show: { opacity: 1, scale: 1 },
    },
  };

  const MotionTag = motion[as] || motion.div;

  return (
    <MotionTag
      ref={ref}
      className={className}
      initial="hidden"
      animate={inView ? "show" : "hidden"}
      variants={variants[variant]}
      transition={{ duration, delay, ease: [0.2, 0.65, 0.3, 0.9] }}
    >
      {children}
    </MotionTag>
  );
}

/**
 * Stagger — animates children in sequence on scroll into view.
 */
export function Stagger({ children, className = "", gap = 0.09, amount = 0.2, once = true }) {
  const ref = useRef(null);
  const inView = useInView(ref, { once, amount });
  return (
    <motion.div
      ref={ref}
      className={className}
      initial="hidden"
      animate={inView ? "show" : "hidden"}
      variants={{
        hidden: {},
        show: { transition: { staggerChildren: gap } },
      }}
    >
      {children}
    </motion.div>
  );
}

export function StaggerItem({ children, className = "", variant = "up" }) {
  const variants = {
    up: { hidden: { opacity: 0, y: 34 }, show: { opacity: 1, y: 0 } },
    scale: { hidden: { opacity: 0, scale: 0.94 }, show: { opacity: 1, scale: 1 } },
  };
  return (
    <motion.div
      className={className}
      variants={variants[variant]}
      transition={{ duration: 0.8, ease: [0.2, 0.65, 0.3, 0.9] }}
    >
      {children}
    </motion.div>
  );
}
